import React from 'react';

import _ from 'lodash';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import Select, { components } from 'react-select';
import tailwindConfig from 'stylesheets/tailwind.config';

import { formatOpeningHours } from 'components/medicalLocations/helpers';

import { resourceShape } from 'components/helpers/serialisableResources';

const { borderWidth, colors, spacing } = tailwindConfig.theme;

export default function MedicalLocationSearchSelect({
  assignableMedicalLocations,
  autoFocus,
  errorMessage,
  isLoading,
  onBlur,
  onInputChange,
  onSelect,
}) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'medicalLocations.MedicalLocationSearchSelect',
  });

  const { t: i18nTShared } = useTranslation('common', {
    keyPrefix: 'shared',
  });

  const options =
    assignableMedicalLocations?.map((assignableMedicalLocation) => ({
      attributes: assignableMedicalLocation.attributes,
      label: assignableMedicalLocation.attributes.name,
      value: assignableMedicalLocation.id,
    })) || [];

  return (
    <Select
      autoFocus={autoFocus}
      blurInputOnSelect={true}
      className='collection-select__select-container tw-h-8 tw-w-full'
      classNamePrefix='collection-select'
      closeMenuOnSelect={true}
      components={{
        DropdownIndicator: () => null,
        IndicatorSeparator: () => null,
        MenuList,
        Option,
      }}
      filterOption={() => true}
      id='assignableMedicalLocations'
      isLoading={isLoading}
      loadingMessage={() => i18nTShared('loading...')}
      name='assignableMedicalLocations'
      noOptionsMessage={() =>
        errorMessage ||
        (assignableMedicalLocations?.length === 0 &&
          i18nT('noOptionsMessage')) ||
        null
      }
      onBlur={onBlur}
      onChange={(option) => onSelect(option.value)}
      onInputChange={onInputChange}
      openMenuOnFocus={false}
      options={options}
      placeholder={i18nT('placeholder')}
      styles={{
        control: (baseStyles) => ({
          ...baseStyles,
          height: `${spacing[8]} !important`,
          minHeight: `${spacing[8]} !important`,
        }),
        input: (baseStyles) => ({
          ...baseStyles,
          alignItems: 'center',
          display: 'flex',
          height: `${spacing[7.5]} !important`,
          minHeight: `${spacing[7.5]} !important`,
        }),
        placeholder: (baseStyles) => ({
          ...baseStyles,
          overflow: 'hidden !important',
          textOverflow: 'ellipsis !important',
          whiteSpace: 'nowrap !important',
          '@media only screen and (max-width: 1199px)': {
            width: `${spacing[46]} !important`,
          },
        }),
        option: (baseStyles) => ({
          ...baseStyles,
          background: `${colors.white} !important`,
          borderBottom: `${borderWidth[1]} solid ${colors.grey[100]} !important`,
          padding: `${spacing[3]} !important`,
        }),
        valueContainer: (baseStyles) => ({
          ...baseStyles,
          height: `${spacing[7.5]} !important`,
          maxWidth: '522.719px',
          minHeight: `${spacing[7.5]} !important`,
          padding: `${spacing[0]} ${spacing[3]} !important`,
          width: '100%',
          '@media only screen and (min-width: 768px)': {
            maxWidth: '279.906px !important',
          },
          '@media only screen and (min-width: 1200px)': {
            maxWidth: '386.578px !important',
          },
        }),
      }}
    />
  );
}

function MenuList(props) {
  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'medicalLocations.MedicalLocationSearchSelect.menuList',
  });

  return (
    <components.MenuList {...props}>
      <div className='tw-sticky tw-top-0 tw-bg-amber-025 tw-p-3 tw-text-s tw-text-amber-800'>
        {i18nT('disclaimer')}
      </div>
      {props.children}
    </components.MenuList>
  );
}

function Option(props) {
  const { t: i18nTHelpers } = useTranslation('components', {
    keyPrefix: 'medicalLocations.helpers',
  });

  const openingHours = formatOpeningHours({
    hours: props.data.attributes.openingHours,
    translations: i18nTHelpers('formatOpeningHours', { returnObjects: true }),
  });

  return (
    <components.Option {...props}>
      <div className='tw-flex tw-flex-col tw-gap-1 tw-text-grey-900'>
        <p className='tw-m-0 tw-text-l tw-font-bold'>
          {props.data.attributes.name}
        </p>
        <div className='tw-text-s'>
          {!!props.data.attributes.address1 && (
            <p className='tw-m-0'>{props.data.attributes.address1},</p>
          )}
          {!!props.data.attributes.address2 && (
            <p className='tw-m-0'>{props.data.attributes.address2},</p>
          )}
          <p className='tw-m-0'>
            {!!props.data.attributes.city && `${props.data.attributes.city}, `}
            {props.data.attributes.postcode}
          </p>
        </div>
        {!!props.data.attributes.phoneNumber && (
          <p className='tw-m-0 tw-text-s'>
            {props.data.attributes.phoneNumber}
          </p>
        )}
        {!!openingHours && <p className='tw-m-0 tw-text-s'>{openingHours}</p>}
      </div>
    </components.Option>
  );
}

MedicalLocationSearchSelect.propTypes = {
  assignableMedicalLocations: PropTypes.arrayOf(
    resourceShape('assignableMedicalLocation'),
  ),
  autoFocus: PropTypes.bool,
  errorMessage: PropTypes.string,
  isLoading: PropTypes.bool,
  onBlur: PropTypes.func.isRequired,
  onInputChange: PropTypes.func.isRequired,
  onSelect: PropTypes.func.isRequired,
};
